import * as React from "react";
import { Check, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  ORDER_DECOPROF_STATUSES,
  type OrderDecoprofStatus,
} from "@/lib/ordersDecoprof";
import BadgeStatus3D from "./BadgeStatus3D";

interface Props {
  status: OrderDecoprofStatus;
  interactive: boolean;
  isPending?: boolean;
  onChange: (next: OrderDecoprofStatus) => void;
}

/**
 * UX-vriendelijke status-picker voor Orders Decoprof.
 * Vervangt het oude "cycle-on-click" gedrag. Klik op badge opent een
 * dropdown met alle statussen (gekleurd) zodat de gebruiker direct
 * de gewenste status kan kiezen.
 */
const OrderStatusDropdown: React.FC<Props> = ({
  status,
  interactive,
  isPending,
  onChange,
}) => {
  if (!interactive) {
    return <BadgeStatus3D status={status} />;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        disabled={isPending}
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className={cn(
            "relative inline-flex",
            isPending && "opacity-70 pointer-events-none",
          )}
        >
          <BadgeStatus3D
            status={status}
            interactive
            title="Klik om status te wijzigen"
          />
          {isPending && (
            <Loader2
              className="absolute -right-3 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground"
              aria-hidden
            />
          )}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        side="bottom"
        sideOffset={6}
        avoidCollisions
        className="w-64 max-h-[320px] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {ORDER_DECOPROF_STATUSES.map((s) => {
          const isCurrent = s === status;
          return (
            <DropdownMenuItem
              key={s}
              className={cn(
                "flex items-center gap-2 cursor-pointer",
                isCurrent && "bg-muted",
              )}
              onClick={(e) => {
                e.stopPropagation();
                if (!isCurrent) onChange(s);
              }}
            >
              <BadgeStatus3D status={s} />
              {isCurrent && (
                <Check size={16} className="ml-auto text-muted-foreground" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrderStatusDropdown;
